import * as Sentry from "@sentry/react";
import type { OperationDefinitionNode } from "graphql";
import type { Exchange, OperationResult } from "urql";
import { pipe, tap } from "wonka";
import { logger } from "../lib/logger";
import { redactVariables } from "./redactVariables";

export const sentryExchange: Exchange = ({ forward }) => {
  return (operations$) => {
    return pipe(
      operations$,
      forward,
      tap((result: OperationResult) => {
        if (result.error) {
          const error = result.error;

          const operation = result.operation;
          const operationName = getOperationName(operation.query);

          Sentry.withScope((scope) => {
            scope.setTag("component", "graphql");
            scope.setTag("operation", operationName || "unknown");
            scope.setContext("graphql", {
              operationName,
              operationType: operation.kind,
              variables: redactVariables(operation.variables),
              query: operation.query.loc?.source.body,
            });

            if (error.networkError) {
              scope.setLevel("error");
              Sentry.captureException(error.networkError);
            }

            if (error.graphQLErrors && error.graphQLErrors.length > 0) {
              error.graphQLErrors.forEach((graphQLError) => {
                scope.setLevel("error");
                Sentry.captureMessage(
                  `GraphQL Error: ${graphQLError.message}`,
                  "error",
                );
              });
            }

            if (
              !error.networkError &&
              (!error.graphQLErrors || error.graphQLErrors.length === 0)
            ) {
              scope.setLevel("error");
              Sentry.captureException(error);
            }
          });
        }
      }),
    );
  };
};

const getOperationName = (query: unknown): string | undefined => {
  try {
    if (query && typeof query === "object" && "definitions" in query) {
      const firstDefinition = (
        query as { definitions?: OperationDefinitionNode[] }
      ).definitions?.[0];
      if (firstDefinition?.kind === "OperationDefinition") {
        return (firstDefinition as OperationDefinitionNode).name?.value;
      }
    }
  } catch (error) {
    logger.error(error instanceof Error ? error : new Error(String(error)), {
      context: "graphql_operation_name",
      query,
    });
    return undefined;
  }
};
